// src/ConsumerApp/DesignSystem/Navigation/Navbar.js

import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../../../assets/theme/theme';
import LogoBB from '../../../assets/icons/logoBB.svg';
import bucket from '../../../assets/icons/bucket_check.svg';
import UserCircle from '../../../assets/icons/UserCircle.svg';
import MagnifyingGlass from '../../../assets/icons/MagnifyingGlass.svg';
import CenteredContainer from '../Layout/CenteredContainer';
import DeliveryAddressModal from '../../Screens/Addresses/DeliveryAddressModal';
import Bucket from '../../Screens/Bucket/Bucket';

// Queries directly owned by Navbar
import { useGetBucketSummary } from '../../api/products/product.hooks';
import { useGetDefaultDeliveryAddress } from '../../api/projects/project.hooks';

export default function Navbar({ searchQuery = '', onSearchChange, onVoiceSearchClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isBucketOpen, setIsBucketOpen] = useState(false);

  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id;

  // 1. Independent data subscriptions
  const { data: addressRes } = useGetDefaultDeliveryAddress(customerId);
  const { data: bucketSummary } = useGetBucketSummary(customerId);

  const cartItemCount = bucketSummary?.buy_now_bucket?.items_count || 0;

  // 2. Separate project label and address locality for Zepto-style formatting
  const { projectName, addressLocality, fullTooltip } = useMemo(() => {
    const addr = addressRes?.data?.address || addressRes?.data || addressRes?.address || addressRes;
    if (!addr) {
      return {
        projectName: '',
        addressLocality: 'Select delivery address',
        fullTooltip: 'Select delivery address',
      };
    }

    const pName = addr.project_name ? addr.project_name.trim() : '';

    const addressParts = [
      addr.area_sector_locality || addr.flat_number || addr.building_name,
      addr.city,
    ].filter(Boolean);

    const locality = addressParts.length > 0 
      ? addressParts.join(', ') 
      : (addr.complete_address || 'Current Site');

    const tooltip = pName ? `${pName} - ${locality}` : locality;

    return {
      projectName: pName,
      addressLocality: locality,
      fullTooltip: tooltip,
    };
  }, [addressRes]);

  // Navigate to /consumer/search if currently on another screen
  const handleSearchInteraction = (e) => {
    if (location.pathname !== '/consumer/search') {
      const targetQuery = searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : '';
      navigate(`/consumer/search${targetQuery}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const queryParam = e.target.value.trim();
      navigate(`/consumer/search?q=${encodeURIComponent(queryParam)}`);
    }
  };

  return (
    <>
      <header style={styles.navShell}>
        <CenteredContainer>
          <div style={styles.navContent}>
            {/* Left: Brand + Zepto-styled Delivery Address */}
            <div style={styles.leftGroup}>
              <div style={styles.brandContainer} onClick={() => navigate('/consumer/HomeScreen')}>
                <img src={LogoBB} alt="Brickbucket" style={styles.brandLogoImg} />
              </div>

              <div style={styles.addressDivider} />

              <div style={styles.deliveryContainer} onClick={() => setIsAddressModalOpen(true)}>
                <div style={styles.deliveryBadge}>
                  <span style={styles.deliveryFlash}>⚡</span>
                  <span style={styles.deliveryTimeText}>Delivery in 30-90 mins</span>
                </div>

                {/* Zepto Inline Formatting */}
                <div style={styles.addressRow} title={fullTooltip}>
                  {projectName ? (
                    <span style={styles.projectNameText}>{projectName}</span>
                  ) : null}

                  {projectName ? (
                    <span style={styles.hyphenSeparator}>-</span>
                  ) : null}

                  <span style={styles.addressText}>{addressLocality}</span>

                  <span style={styles.caretDown}>▼</span>
                </div>
              </div>
            </div>

            {/* Center: Search Field */}
            <div
              style={styles.searchContainer}
              onClick={handleSearchInteraction}
            >
              <img
                src={MagnifyingGlass}
                alt="search"
                style={styles.magnifyingGlassIcon}
              />
              <input
                type="text"
                placeholder='Search for "tile adhesive", pipes, tools...'
                value={searchQuery}
                onClick={handleSearchInteraction}
                onFocus={handleSearchInteraction}
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                  if (location.pathname !== '/consumer/search') {
                    navigate(`/consumer/search?q=${encodeURIComponent(e.target.value)}`);
                  } else if (onSearchChange) {
                    onSearchChange(e.target.value);
                  }
                }}
                style={styles.searchInput}
              />
              {onVoiceSearchClick && (
                <button
                  style={styles.micBtn}
                  title="Voice Search"
                  onClick={(e) => {
                    e.stopPropagation();
                    onVoiceSearchClick();
                  }}
                  type="button"
                >
                  🎤
                </button>
              )}
            </div>

            {/* Right: Account & Bucket Trigger */}
            <div style={styles.rightGroup}>
              <button
                style={styles.accountIconBtn}
                onClick={() => navigate('/consumer/AccountScreen')}
                title="Account"
                type="button"
              >
                <img src={UserCircle} alt="Account" style={styles.userIconImg} />
              </button>

              <button
                style={styles.bucketPillBtn}
                onClick={() => setIsBucketOpen(true)}
                title="View Bucket"
                type="button"
              >
                <div style={styles.bucketIconWrap}>
                  <img src={bucket} alt="Bucket" style={styles.bucketImg} />
                  {cartItemCount > 0 && <span style={styles.bucketBadge}>{cartItemCount}</span>}
                </div>
              </button>
            </div>
          </div>
        </CenteredContainer>
      </header>

      {/* Zepto-Style Location Picker Modal */}
      <DeliveryAddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        onAddNewAddress={(loc) => navigate('/consumer/account/delivery/new', { state: loc })}
      />

      <Bucket isOpen={isBucketOpen} onClose={() => setIsBucketOpen(false)} />
    </>
  );
}

const styles = {
  navShell: {
    backgroundColor: theme.colors.pureWhite,
    borderBottom: `1px solid ${theme.colors.grey005}`,
    position: 'sticky',
    top: 0,
    zIndex: 100,
    ...theme.shadows.fieldShadow,
    minHeight: 76,
    display: 'flex',
    alignItems: 'center',
  },
  navContent: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 0',
    gap: theme.spacing[300],
  },
  leftGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[200],
    flexShrink: 0,
  },
  brandContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  brandLogoImg: {
    height: 52,
    width: 'auto',
    maxHeight: 56,
    objectFit: 'contain',
    display: 'block',
  },
  addressDivider: {
    width: 1,
    height: 34,
    backgroundColor: theme.colors.grey005,
  },
  deliveryContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    cursor: 'pointer',
    maxWidth: 240,
  },
  deliveryBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  deliveryFlash: {
    fontSize: 13,
    color: theme.colors.gold,
  },
  deliveryTimeText: {
    fontFamily: theme.typography.headingMD.fontFamily,
    fontSize: 14,
    fontWeight: '700',
    color: theme.colors.charcoal,
    lineHeight: '16px',
  },
  addressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  projectNameText: {
    fontSize: 12.5,
    fontWeight: '700',
    color: theme.colors.charcoal,
    flexShrink: 0,
    maxWidth: 110,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  hyphenSeparator: {
    fontSize: 12,
    color: theme.colors.concrete,
    flexShrink: 0,
    padding: '0 1px',
  },
  addressText: {
    ...theme.typography.bodySM,
    fontSize: 12,
    color: theme.colors.concrete,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  caretDown: {
    fontSize: 8,
    color: theme.colors.concrete,
    marginLeft: 2,
    flexShrink: 0,
  },
  searchContainer: {
    flex: 1,
    maxWidth: 580,
    minWidth: 280,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.colors.pureWhite,
    border: `1.5px solid ${theme.colors.grey005}`,
    borderRadius: theme.radius[12],
    padding: '10px 16px',
    gap: 12,
    cursor: 'text',
    boxSizing: 'border-box',
  },
  magnifyingGlassIcon: {
    width: 16,
    height: 16,
    opacity: 0.55,
    display: 'block',
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    ...theme.typography.bodyMD,
    backgroundColor: 'transparent',
    color: theme.colors.charcoal,
    cursor: 'text',
    padding: 0,
  },
  micBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 15,
  },
  rightGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[200],
    flexShrink: 0,
  },
  accountIconBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: theme.radius[999],
  },
  userIconImg: {
    width: 32,
    height: 32,
    objectFit: 'contain',
    display: 'block',
  },
  bucketPillBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    backgroundColor: theme.colors.brand,
    color: theme.colors.pureWhite,
    border: 'none',
    borderRadius: 999,
    padding: '10px 18px',
    cursor: 'pointer',
    ...theme.shadows.cardShadow,
  },
  bucketIconWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bucketImg: {
    width: 22,
    height: 22,
    objectFit: 'contain',
    display: 'block',
  },
  bucketBadge: {
    position: 'absolute',
    top: -8,
    right: -10,
    backgroundColor: theme.colors.pureWhite,
    color: theme.colors.brand,
    fontSize: 10,
    fontWeight: '800',
    borderRadius: theme.radius[999],
    minWidth: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 3px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
  },
};